import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { engineKeys } from "@/academy/engines";
import { previewPublishReadiness, setPublishStatus } from "@/lib/academy.functions";
import { getLessonValidationReport, type LessonValidationRow } from "@/lib/academy-validation.functions";

export const Route = createFileRoute("/_authenticated/admin/academy")({
  head: () => ({ meta: [{ title: "Admin · Academy" }, { name: "robots", content: "noindex" }] }),
  component: AdminAcademy,
});

type Status = "draft" | "review" | "published";
type Domain = { id: string; slug: string; name: string; engine_key: string | null; tier: string; status: Status };
type LessonSection = { heading: string; body_md: string; provenance?: string };
type QuizItem = { q: string; options: string[]; answer: number; explain?: string };
type MasteryRule = { required_indices: number[] | null; min_correct_attempts: number };
type Lesson = {
  id: string; slug: string; title: string; summary: string | null; body_md: string;
  status: Status; tier: string; difficulty: string; est_minutes: number;
  learning_objectives: string[];
  sections: LessonSection[] | null;
  prompts: { reflection?: string[]; practice?: string } | null;
  quiz: { questions?: QuizItem[] } | null;
  provenance: Record<string, string> | null;
  scientific_context: string | null;
  historical_context: string | null;
  traditional_context: string | null;
  kaf_original: string | null;
  word_count: number;
  applications: unknown;
  mastery_rule: MasteryRule | null;
};

const STATUS_LABEL: Record<Status, string> = { draft: "Draft", review: "In review", published: "Published" };
const STATUS_COLOR: Record<Status, string> = {
  draft: "text-foreground/40",
  review: "text-amber-400",
  published: "text-emerald-400",
};

function StatusPill({ status }: { status: Status }) {
  return (
    <span className={`text-[10px] uppercase tracking-widest ${STATUS_COLOR[status]}`}>
      {STATUS_LABEL[status]}
    </span>
  );
}

/**
 * Wraps a status change with the readiness preview so admins see gate reasons
 * before the DB trigger rejects the write.
 */
async function guardedSetStatus(
  preview: (args: { data: { table: "academy_lessons" | "academy_domains"; id: string } }) => Promise<{ ok: boolean; reasons: string[] }>,
  setStatus: (args: { data: { table: "academy_lessons" | "academy_domains"; id: string; status: Status } }) => Promise<{ ok: boolean }>,
  table: "academy_lessons" | "academy_domains",
  id: string,
  status: Status,
) {
  if (status === "published") {
    const r = await preview({ data: { table, id } });
    if (!r.ok) {
      toast.error(`Cannot publish yet: ${r.reasons.join(" ")}`);
      return false;
    }
  }
  try {
    await setStatus({ data: { table, id, status } });
    toast.success(`Status → ${STATUS_LABEL[status]}`);
    return true;
  } catch (e) {
    toast.error(e instanceof Error ? e.message : "Failed to update status");
    return false;
  }
}

function AdminAcademy() {
  const preview = useServerFn(previewPublishReadiness);
  const setStatus = useServerFn(setPublishStatus);

  const [lessons, setLessons] = useState<Lesson[]>([]);
  const [sel, setSel] = useState<Lesson | null>(null);
  const [saving, setSaving] = useState(false);
  const [validationTick, setValidationTick] = useState(0);
  const [validationRows, setValidationRows] = useState<LessonValidationRow[]>([]);
  const selValidation = sel ? validationRows.find((r) => r.lesson_id === sel.id) ?? null : null;

  async function refresh() {
    const { data } = await supabase
      .from("academy_lessons")
      .select("id, slug, title, summary, body_md, status, tier, difficulty, est_minutes, learning_objectives, sections, prompts, quiz, provenance, scientific_context, historical_context, traditional_context, kaf_original, word_count, applications, mastery_rule")
      .order("updated_at", { ascending: false });
    setLessons((data as unknown as Lesson[]) ?? []);
  }
  useEffect(() => { refresh(); }, []);

  function countWords(l: Lesson) {
    const bodyWords = (l.body_md ?? "").trim().split(/\s+/).filter(Boolean).length;
    const sectionWords = (l.sections ?? []).reduce((sum, s) => sum + (s.body_md ?? "").trim().split(/\s+/).filter(Boolean).length, 0);
    const ctxWords = [l.scientific_context, l.historical_context, l.traditional_context, l.kaf_original]
      .filter(Boolean)
      .reduce((sum, t) => sum + (t as string).trim().split(/\s+/).filter(Boolean).length, 0);
    return bodyWords + sectionWords + ctxWords;
  }


  async function saveContent() {
    if (!sel) return;
    setSaving(true);
    const word_count = countWords(sel);
    const { error } = await supabase.from("academy_lessons").update({
      title: sel.title, summary: sel.summary, body_md: sel.body_md,
      tier: sel.tier, difficulty: sel.difficulty, est_minutes: sel.est_minutes,
      learning_objectives: sel.learning_objectives,
      sections: sel.sections ?? [],
      prompts: sel.prompts ?? {},
      quiz: sel.quiz ?? {},
      provenance: sel.provenance ?? {},
      scientific_context: sel.scientific_context,
      historical_context: sel.historical_context,
      traditional_context: sel.traditional_context,
      kaf_original: sel.kaf_original,
      mastery_rule: sel.mastery_rule ?? null,
      word_count,
    } as never).eq("id", sel.id);
    setSaving(false);
    if (error) toast.error(error.message);
    else {
      toast.success(`Saved · ${word_count.toLocaleString()} words`);
      refresh();
      setValidationTick((t) => t + 1);
    }
  }

  async function changeStatus(next: Status) {
    if (!sel) return;
    const ok = await guardedSetStatus(preview, setStatus, "academy_lessons", sel.id, next);
    if (ok) { setSel({ ...sel, status: next }); refresh(); }
  }

  return (
    <section>
      <h2 className="font-serif text-2xl text-gold-luminous">Academy Lessons</h2>
      <p className="mt-1 text-sm text-muted-foreground">Draft → In review → Published. Content is guarded before it can go live.</p>

      <div className="mt-6 grid gap-6 lg:grid-cols-[320px_1fr]">
        <ul className="max-h-[70vh] space-y-2 overflow-auto">
          {lessons.map((l) => (
            <li key={l.id}>
              <button
                onClick={() => setSel(l)}
                className={`w-full min-h-[44px] rounded-md border p-3 text-left text-sm ${sel?.id === l.id ? "border-gold bg-gold/5" : "hairline hover:border-gold/50"}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="line-clamp-1 font-medium">{l.title}</span>
                  <StatusPill status={l.status} />
                </div>
                <p className="mt-1 text-xs text-foreground/50">{l.slug}</p>
              </button>
            </li>
          ))}
        </ul>

        {sel ? (
          <div className="space-y-3 rounded-xl border hairline p-4">
            <input value={sel.title} onChange={(e) => setSel({ ...sel, title: e.target.value })}
              className="w-full rounded-md border hairline bg-obsidian px-3 py-2" />
            <textarea value={sel.summary ?? ""} onChange={(e) => setSel({ ...sel, summary: e.target.value })}
              placeholder="Summary" className="min-h-[60px] w-full rounded-md border hairline bg-obsidian p-3 text-sm" />
            <textarea value={sel.body_md} onChange={(e) => setSel({ ...sel, body_md: e.target.value })}
              placeholder="Markdown intro (structured sections below)" className="min-h-[220px] w-full rounded-md border hairline bg-obsidian p-3 font-mono text-sm" />

            <DepthPanel lesson={sel} onChange={(patch) => setSel({ ...sel, ...patch })} />
            <MasteryRulePanel lesson={sel} onChange={(rule) => setSel({ ...sel, mastery_rule: rule })} />
            <div className="flex flex-wrap items-center gap-3">
              <label className="flex items-center gap-2 text-sm">
                <span className="text-xs uppercase tracking-widest text-foreground/60">Status</span>
                <select
                  value={sel.status}
                  onChange={(e) => changeStatus(e.target.value as Status)}
                  className="rounded-md border hairline bg-obsidian px-2 py-1 text-sm"
                >
                  <option value="draft">Draft</option>
                  <option value="review">In review</option>
                  <option value="published">Published</option>
                </select>
              </label>
              <select value={sel.tier} onChange={(e) => setSel({ ...sel, tier: e.target.value })}
                className="rounded-md border hairline bg-obsidian px-2 py-1 text-sm">
                <option value="free">Free</option><option value="premium">Premium</option>
              </select>
              <select value={sel.difficulty} onChange={(e) => setSel({ ...sel, difficulty: e.target.value })}
                className="rounded-md border hairline bg-obsidian px-2 py-1 text-sm">
                <option value="beginner">Beginner</option>
                <option value="intermediate">Intermediate</option>
                <option value="advanced">Advanced</option>
                <option value="master">Master</option>
              </select>
              <input type="number" value={sel.est_minutes} onChange={(e) => setSel({ ...sel, est_minutes: Number(e.target.value) })}
                className="w-24 rounded-md border hairline bg-obsidian px-2 py-1 text-sm" />
              <Button onClick={saveContent} disabled={saving}>{saving ? "Saving…" : "Save content"}</Button>
              <InlineValidationBadge row={selValidation} />
            </div>
          </div>
        ) : (
          <p className="text-sm text-foreground/60">Select a lesson to edit.</p>
        )}
      </div>

      <ValidationPanel
        selectedId={sel?.id ?? null}
        refreshKey={validationTick}
        onReport={setValidationRows}
        onPick={(id) => {
          const l = lessons.find((x) => x.id === id);
          if (l) setSel(l);
        }}
      />

      <DomainsEditor />
    </section>
  );
}

function InlineValidationBadge({ row }: { row: LessonValidationRow | null }) {
  if (!row) {
    return <span className="text-[11px] uppercase tracking-widest text-foreground/40">Validation: —</span>;
  }
  if (row.issues.length === 0) {
    return (
      <span className="rounded-full border border-emerald-500/40 bg-emerald-500/5 px-3 py-1 text-[11px] uppercase tracking-widest text-emerald-300">
        ✓ Passes depth standard
      </span>
    );
  }
  return (
    <span
      title={row.issues.join("\n")}
      className="rounded-full border border-amber-500/40 bg-amber-500/5 px-3 py-1 text-[11px] uppercase tracking-widest text-amber-300"
    >
      ✗ {row.issues.length} field{row.issues.length === 1 ? "" : "s"} failing
    </span>
  );
}

function ValidationPanel({ selectedId, onPick, refreshKey, onReport }: {
  selectedId: string | null;
  onPick: (id: string) => void;
  refreshKey: number;
  onReport: (rows: LessonValidationRow[]) => void;
}) {
  const load = useServerFn(getLessonValidationReport);
  const [rows, setRows] = useState<LessonValidationRow[]>([]);
  const [totals, setTotals] = useState<{ lessons: number; failing: number; passing: number } | null>(null);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState<"failing" | "all">("failing");

  async function run() {
    setLoading(true);
    try {
      const r = await load({});
      setRows(r.rows);
      setTotals(r.totals);
      onReport(r.rows);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Validation failed");
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { run(); }, [refreshKey]);

  const shown = filter === "failing" ? rows.filter((r) => r.issues.length > 0) : rows;

  return (
    <div className="mt-12">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-serif text-2xl text-gold-luminous">Publish validation</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Which draft/review lessons still fail the depth standard, and which fields need work before publishing.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select value={filter} onChange={(e) => setFilter(e.target.value as "failing" | "all")}
            className="rounded-md border hairline bg-obsidian px-2 py-1 text-sm">
            <option value="failing">Failing only</option>
            <option value="all">All draft & review</option>
          </select>
          <Button variant="outline" onClick={run} disabled={loading}>
            {loading ? "Checking…" : "Re-run"}
          </Button>
        </div>
      </div>

      {totals && (
        <div className="mt-3 flex flex-wrap gap-2 text-[11px] uppercase tracking-widest text-foreground/60">
          <span className="rounded-full border hairline px-3 py-1">{totals.lessons} unpublished</span>
          <span className="rounded-full border border-emerald-500/30 px-3 py-1 text-emerald-400">
            {totals.passing} ready
          </span>
          <span className="rounded-full border border-amber-500/30 px-3 py-1 text-amber-400">
            {totals.failing} need work
          </span>
        </div>
      )}

      <div className="mt-4 overflow-x-auto rounded-xl border hairline">
        <table className="w-full text-sm">
          <thead className="bg-obsidian/60 text-left text-xs uppercase tracking-widest text-foreground/60">
            <tr>
              <th className="p-3">Lesson</th>
              <th className="p-3">Status</th>
              <th className="p-3">Failing fields</th>
              <th className="p-3"></th>
            </tr>
          </thead>
          <tbody>
            {shown.length === 0 && (
              <tr><td className="p-6 text-center text-sm text-foreground/50" colSpan={4}>
                {loading ? "Loading…" : filter === "failing" ? "🎉 Every unpublished lesson passes." : "No draft or review lessons."}
              </td></tr>
            )}
            {shown.map((r) => {
              const ok = r.issues.length === 0;
              return (
                <tr key={r.lesson_id}
                  className={`border-t hairline ${selectedId === r.lesson_id ? "bg-gold/5" : ""}`}>
                  <td className="p-3">
                    <div className="font-medium">{r.title}</div>
                    <div className="text-xs text-foreground/50">{r.slug}</div>
                  </td>
                  <td className="p-3"><StatusPill status={r.status as Status} /></td>
                  <td className="p-3">
                    {ok ? (
                      <span className="text-xs text-emerald-400">Ready to publish</span>
                    ) : (
                      <ul className="space-y-1">
                        {r.issues.map((i, k) => {
                          const field = i.split(":")[0];
                          const rest = i.slice(field.length + 1).trim();
                          return (
                            <li key={k} className="flex flex-wrap items-center gap-2">
                              <span className="rounded-full border border-amber-500/30 bg-amber-500/5 px-2 py-0.5 text-[10px] uppercase tracking-widest text-amber-300">
                                {field}
                              </span>
                              <span className="text-xs text-foreground/70">{rest || i}</span>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </td>
                  <td className="p-3 text-right">
                    <button
                      type="button"
                      onClick={() => onPick(r.lesson_id)}
                      className="rounded-md border hairline px-3 py-1 text-xs hover:border-gold/50"
                    >
                      Edit
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function DomainsEditor() {
  const preview = useServerFn(previewPublishReadiness);
  const setStatus = useServerFn(setPublishStatus);
  const [rows, setRows] = useState<Domain[]>([]);

  async function refresh() {
    const { data } = await supabase
      .from("academy_domains")
      .select("id, slug, name, engine_key, tier, status")
      .order("sort_order");
    setRows((data as Domain[]) ?? []);
  }
  useEffect(() => { refresh(); }, []);

  async function update(id: string, patch: Partial<Domain>) {
    const { error } = await supabase.from("academy_domains").update(patch).eq("id", id);
    if (error) toast.error(error.message); else { toast.success("Domain updated"); refresh(); }
  }

  async function changeStatus(id: string, next: Status) {
    const ok = await guardedSetStatus(preview, setStatus, "academy_domains", id, next);
    if (ok) refresh();
  }

  return (
    <div className="mt-12">
      <h2 className="font-serif text-2xl text-gold-luminous">Domains</h2>
      <p className="mt-1 text-sm text-muted-foreground">Engine, tier, and publish state per knowledge domain.</p>
      <div className="mt-4 overflow-x-auto rounded-xl border hairline">
        <table className="w-full text-sm">
          <thead className="bg-obsidian/60 text-left text-xs uppercase tracking-widest text-foreground/60">
            <tr><th className="p-3">Domain</th><th className="p-3">Engine</th><th className="p-3">Tier</th><th className="p-3">Status</th></tr>
          </thead>
          <tbody>
            {rows.map((d) => (
              <tr key={d.id} className="border-t hairline">
                <td className="p-3"><div className="font-medium">{d.name}</div><div className="text-xs text-foreground/50">{d.slug}</div></td>
                <td className="p-3">
                  <select value={d.engine_key ?? ""} onChange={(e) => update(d.id, { engine_key: e.target.value || null })}
                    className="rounded-md border hairline bg-obsidian px-2 py-1">
                    <option value="">(generic)</option>
                    {engineKeys.map((k) => <option key={k} value={k}>{k}</option>)}
                  </select>
                </td>
                <td className="p-3">
                  <select value={d.tier} onChange={(e) => update(d.id, { tier: e.target.value })}
                    className="rounded-md border hairline bg-obsidian px-2 py-1">
                    <option value="free">free</option><option value="premium">premium</option><option value="pro">pro</option>
                  </select>
                </td>
                <td className="p-3">
                  <select
                    value={d.status}
                    onChange={(e) => changeStatus(d.id, e.target.value as Status)}
                    className="rounded-md border hairline bg-obsidian px-2 py-1"
                  >
                    <option value="draft">Draft</option>
                    <option value="review">In review</option>
                    <option value="published">Published</option>
                  </select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const PROVENANCE_KINDS = ["evidence", "historical", "traditional", "kaf_original"] as const;

function DepthPanel({ lesson, onChange }: {
  lesson: Lesson;
  onChange: (patch: Partial<Lesson>) => void;
}) {
  const objectives = lesson.learning_objectives ?? [];
  const sections = lesson.sections ?? [];
  const reflection = lesson.prompts?.reflection ?? [];
  const practice = lesson.prompts?.practice ?? "";
  const quiz = lesson.quiz?.questions ?? [];

  function setObjectives(next: string[]) { onChange({ learning_objectives: next }); }
  function setSections(next: LessonSection[]) { onChange({ sections: next }); }
  function setPrompts(next: { reflection?: string[]; practice?: string }) { onChange({ prompts: next }); }
  function setQuiz(next: QuizItem[]) { onChange({ quiz: { questions: next } }); }

  const wc = ((): number => {
    const b = (lesson.body_md ?? "").trim().split(/\s+/).filter(Boolean).length;
    const s = sections.reduce((sum, x) => sum + (x.body_md ?? "").trim().split(/\s+/).filter(Boolean).length, 0);
    const c = [lesson.scientific_context, lesson.historical_context, lesson.traditional_context, lesson.kaf_original]
      .filter(Boolean).reduce((sum, t) => sum + (t as string).trim().split(/\s+/).filter(Boolean).length, 0);
    return b + s + c;
  })();

  const gates = {
    objectives: objectives.filter((o) => o.trim()).length >= 3,
    depth: sections.length >= 3 || wc >= 1500,
    prompts: reflection.filter((r) => r.trim()).length + (practice.trim() ? 1 : 0) >= 2,
    quiz: quiz.length >= 3,
  };

  return (
    <div className="space-y-6 rounded-xl border border-gold/20 bg-gold/[0.03] p-4">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-xs uppercase tracking-[0.3em] text-gold">Knowledge Depth</h3>
          <p className="mt-1 text-xs text-foreground/60">University-grade lessons only. Meet all gates to publish.</p>
        </div>
        <p className="text-[10px] uppercase tracking-widest text-foreground/50">
          {wc.toLocaleString()} words · {sections.length} sections · {quiz.length} quiz
        </p>
      </header>

      <div className="grid grid-cols-2 gap-2 text-[11px] sm:grid-cols-4">
        <Gate ok={gates.objectives} label="≥3 objectives" />
        <Gate ok={gates.depth} label="≥3 sections or 1,500 words" />
        <Gate ok={gates.prompts} label="≥2 prompts" />
        <Gate ok={gates.quiz} label="≥3 quiz items" />
      </div>

      {/* Objectives */}
      <div>
        <div className="mb-2 flex items-center justify-between">
          <span className="text-xs uppercase tracking-widest text-foreground/60">Learning objectives</span>
          <button type="button" onClick={() => setObjectives([...objectives, ""])}
            className="rounded-full border hairline px-3 py-1 text-xs">+ Add</button>
        </div>
        <ul className="space-y-2">
          {objectives.map((o, i) => (
            <li key={i} className="flex gap-2">
              <input value={o} onChange={(e) => { const n = [...objectives]; n[i] = e.target.value; setObjectives(n); }}
                className="flex-1 rounded-md border hairline bg-obsidian px-3 py-2 text-sm" />
              <button type="button" onClick={() => setObjectives(objectives.filter((_, j) => j !== i))}
                className="rounded-md border hairline px-2 text-xs text-foreground/60">✕</button>
            </li>
          ))}
        </ul>
      </div>

      {/* Sections */}
      <div>
        <div className="mb-2 flex items-center justify-between">
          <span className="text-xs uppercase tracking-widest text-foreground/60">Structured sections</span>
          <button type="button" onClick={() => setSections([...sections, { heading: "", body_md: "", provenance: "evidence" }])}
            className="rounded-full border hairline px-3 py-1 text-xs">+ Add section</button>
        </div>
        <div className="space-y-3">
          {sections.map((s, i) => (
            <div key={i} className="space-y-2 rounded-md border hairline p-3">
              <div className="flex gap-2">
                <input value={s.heading} onChange={(e) => { const n = [...sections]; n[i] = { ...s, heading: e.target.value }; setSections(n); }}
                  placeholder="Section heading"
                  className="flex-1 rounded-md border hairline bg-obsidian px-3 py-2 text-sm" />
                <select value={s.provenance ?? "evidence"}
                  onChange={(e) => { const n = [...sections]; n[i] = { ...s, provenance: e.target.value }; setSections(n); }}
                  className="rounded-md border hairline bg-obsidian px-2 py-1 text-xs">
                  {PROVENANCE_KINDS.map((k) => <option key={k} value={k}>{k}</option>)}
                </select>
                <button type="button" onClick={() => setSections(sections.filter((_, j) => j !== i))}
                  className="rounded-md border hairline px-2 text-xs text-foreground/60">✕</button>
              </div>
              <textarea value={s.body_md} onChange={(e) => { const n = [...sections]; n[i] = { ...s, body_md: e.target.value }; setSections(n); }}
                placeholder="Section body (markdown)"
                className="min-h-[140px] w-full rounded-md border hairline bg-obsidian p-3 font-mono text-xs" />
            </div>
          ))}
        </div>
      </div>

      {/* Provenance contexts */}
      <div className="grid gap-3 sm:grid-cols-2">
        <ContextField label="Scientific context (evidence)" value={lesson.scientific_context ?? ""}
          onChange={(v) => onChange({ scientific_context: v })} />
        <ContextField label="Historical context" value={lesson.historical_context ?? ""}
          onChange={(v) => onChange({ historical_context: v })} />
        <ContextField label="Traditional context" value={lesson.traditional_context ?? ""}
          onChange={(v) => onChange({ traditional_context: v })} />
        <ContextField label="KAF original synthesis" value={lesson.kaf_original ?? ""}
          onChange={(v) => onChange({ kaf_original: v })} />
      </div>

      {/* Prompts */}
      <div>
        <div className="mb-2 flex items-center justify-between">
          <span className="text-xs uppercase tracking-widest text-foreground/60">Reflection prompts</span>
          <button type="button" onClick={() => setPrompts({ ...lesson.prompts, reflection: [...reflection, ""] })}
            className="rounded-full border hairline px-3 py-1 text-xs">+ Add</button>
        </div>
        <ul className="space-y-2">
          {reflection.map((r, i) => (
            <li key={i} className="flex gap-2">
              <input value={r}
                onChange={(e) => { const n = [...reflection]; n[i] = e.target.value; setPrompts({ ...lesson.prompts, reflection: n }); }}
                className="flex-1 rounded-md border hairline bg-obsidian px-3 py-2 text-sm" />
              <button type="button"
                onClick={() => setPrompts({ ...lesson.prompts, reflection: reflection.filter((_, j) => j !== i) })}
                className="rounded-md border hairline px-2 text-xs text-foreground/60">✕</button>
            </li>
          ))}
        </ul>
        <textarea value={practice}
          onChange={(e) => setPrompts({ ...lesson.prompts, practice: e.target.value })}
          placeholder="Embodied practice…"
          className="mt-3 min-h-[80px] w-full rounded-md border hairline bg-obsidian p-3 text-sm" />
      </div>

      {/* Quiz */}
      <div>
        <div className="mb-2 flex items-center justify-between">
          <span className="text-xs uppercase tracking-widest text-foreground/60">Knowledge check</span>
          <button type="button"
            onClick={() => setQuiz([...quiz, { q: "", options: ["", "", "", ""], answer: 0 }])}
            className="rounded-full border hairline px-3 py-1 text-xs">+ Add question</button>
        </div>
        <div className="space-y-3">
          {quiz.map((q, i) => (
            <div key={i} className="space-y-2 rounded-md border hairline p-3">
              <div className="flex gap-2">
                <input value={q.q} onChange={(e) => { const n = [...quiz]; n[i] = { ...q, q: e.target.value }; setQuiz(n); }}
                  placeholder={`Question ${i + 1}`}
                  className="flex-1 rounded-md border hairline bg-obsidian px-3 py-2 text-sm" />
                <button type="button" onClick={() => setQuiz(quiz.filter((_, j) => j !== i))}
                  className="rounded-md border hairline px-2 text-xs text-foreground/60">✕</button>
              </div>
              <div className="grid gap-2 sm:grid-cols-2">
                {q.options.map((opt, j) => (
                  <label key={j} className={`flex items-center gap-2 rounded-md border p-2 text-sm ${q.answer === j ? "border-emerald-500/60 bg-emerald-500/5" : "hairline"}`}>
                    <input type="radio" name={`q-${i}`} checked={q.answer === j}
                      onChange={() => { const n = [...quiz]; n[i] = { ...q, answer: j }; setQuiz(n); }} />
                    <input value={opt}
                      onChange={(e) => { const n = [...quiz]; const opts = [...q.options]; opts[j] = e.target.value; n[i] = { ...q, options: opts }; setQuiz(n); }}
                      placeholder={`Option ${j + 1}`}
                      className="flex-1 bg-transparent text-sm outline-none" />
                  </label>
                ))}
              </div>
              <input value={q.explain ?? ""}
                onChange={(e) => { const n = [...quiz]; n[i] = { ...q, explain: e.target.value }; setQuiz(n); }}
                placeholder="Explanation shown after answering"
                className="w-full rounded-md border hairline bg-obsidian px-3 py-2 text-xs text-foreground/70" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function Gate({ ok, label }: { ok: boolean; label: string }) {
  return (
    <div className={`rounded-md border px-2 py-1 text-center ${ok ? "border-emerald-500/50 text-emerald-300" : "border-foreground/20 text-foreground/50"}`}>
      {ok ? "✓" : "○"} {label}
    </div>
  );
}

function ContextField({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  return (
    <label className="block">
      <span className="text-[11px] uppercase tracking-widest text-foreground/60">{label}</span>
      <textarea value={value} onChange={(e) => onChange(e.target.value)}
        className="mt-1 min-h-[100px] w-full rounded-md border hairline bg-obsidian p-3 text-sm" />
    </label>
  );
}


function extractScenarioCount(applications: unknown): number {
  if (!applications) return 0;
  const arr = Array.isArray(applications)
    ? applications
    : Array.isArray((applications as { scenarios?: unknown }).scenarios)
      ? (applications as { scenarios: unknown[] }).scenarios
      : [];
  return arr.filter((s) => s && Array.isArray((s as { options?: unknown }).options)).length;
}

function MasteryRulePanel({ lesson, onChange }: {
  lesson: Lesson;
  onChange: (rule: MasteryRule | null) => void;
}) {
  const total = extractScenarioCount(lesson.applications);
  const rule = lesson.mastery_rule ?? { required_indices: null, min_correct_attempts: 1 };
  const all = rule.required_indices === null;
  const selected = new Set(rule.required_indices ?? []);

  function toggle(i: number) {
    const next = new Set(selected);
    if (next.has(i)) next.delete(i); else next.add(i);
    onChange({ required_indices: Array.from(next).sort((a, b) => a - b), min_correct_attempts: rule.min_correct_attempts });
  }

  return (
    <div className="rounded-xl border hairline p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <p className="text-xs uppercase tracking-widest text-gold">Mastery rule</p>
          <p className="mt-1 text-xs text-foreground/60">
            Choose which scenarios count and how many correct attempts each requires to mark a lesson Ready.
            {total > 0 ? ` This lesson has ${total} scenario${total === 1 ? "" : "s"}.` : " No scenarios detected in this lesson's applications."}
          </p>
        </div>
        <label className="flex items-center gap-2 text-xs">
          <span className="uppercase tracking-widest text-foreground/60">Min correct attempts</span>
          <input
            type="number" min={1} max={10}
            value={rule.min_correct_attempts}
            onChange={(e) => onChange({
              required_indices: rule.required_indices,
              min_correct_attempts: Math.max(1, Math.min(10, Number(e.target.value) || 1)),
            })}
            className="w-20 rounded-md border hairline bg-obsidian px-2 py-1 text-sm"
          />
        </label>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <label className="inline-flex items-center gap-2 text-xs">
          <input
            type="checkbox"
            checked={all}
            onChange={(e) => onChange({
              required_indices: e.target.checked ? null : [],
              min_correct_attempts: rule.min_correct_attempts,
            })}
          />
          Every scenario counts
        </label>
        {!all && total > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {Array.from({ length: total }, (_, i) => (
              <button
                key={i}
                type="button"
                onClick={() => toggle(i)}
                className={`min-h-[32px] rounded-full border px-3 py-1 text-[11px] uppercase tracking-widest ${
                  selected.has(i) ? "border-gold bg-gold/10 text-gold" : "hairline text-foreground/60 hover:border-gold/40"
                }`}
                aria-pressed={selected.has(i)}
              >
                Scenario {i + 1}
              </button>
            ))}
          </div>
        )}
      </div>

      {!all && selected.size === 0 && total > 0 && (
        <p className="mt-2 text-[11px] text-amber-300">
          No scenarios selected — learners cannot reach Ready on this lesson.
        </p>
      )}

      <button
        type="button"
        onClick={() => onChange(null)}
        className="mt-3 text-[11px] uppercase tracking-widest text-foreground/50 hover:text-gold"
      >
        Reset to default (all count · 1 attempt)
      </button>
    </div>
  );
}
